import { useEffect, useState } from 'react';
import { API_NftDetails } from 'shared';
import useTokensList from './useTokensList';
import fetcher from '../../utils/fetcher';
import { AxiosError } from 'axios';
import { ejectErrorMessage } from '../../utils/utils';

type Error = {
  status: boolean;
  message?: string;
};

export interface IUseFreeNft {
  nft: API_NftDetails | undefined;
  noAvailable: boolean;
  error: Error;
  loading: boolean;
  nftLoading: boolean;
  handleLeftClick: () => void;
  handleRightClick: () => void;
  update: (callback: () => void) => void;
}

const useFreeNft = (initialToken?: string): IUseFreeNft => {
  const [nft, setNft] = useState<API_NftDetails | undefined>(undefined);
  const [noAvailable, setNoAvailable] = useState(false);
  const [error, setError] = useState<Error>({ status: false });
  const [loading, setLoading] = useState<boolean>(true);
  const [nftLoading, setNftLoading] = useState<boolean>(false);
  const tokensList = useTokensList();

  useEffect(() => {
    getFreeNft(initialToken, (nft) => {
      tokensList.addTokenToEnd(nft.token);
    });
  }, []);

  const getFreeNft = (
    exactToken?: string,
    callback?: (nft: API_NftDetails) => void
  ) => {
    fetcher.market
      .getFreeNft(tokensList.getFetched(), exactToken)
      .then((response) => {
        setNft(response.nft);
        if (response.noMore) {
          tokensList.setNoMore();
        }
        if (callback) callback(response.nft);
      })
      .catch((err: AxiosError) => {
        if (err.response?.status === 404) {
          setNoAvailable(true);
        } else {
          setError({ status: true, message: ejectErrorMessage(err) });
        }
      })
      .finally(() => setLoading(false));
  };

  const moveToSide = (
    tokenGetter: () => string | undefined,
    tokenAdder: (token: string) => void
  ) => {
    setNftLoading(true);
    const token = tokenGetter();
    if (token) {
      getFreeNft(token, () => setNftLoading(false));
    } else {
      getFreeNft(undefined, (nft) => {
        tokenAdder(nft.token);
        setNftLoading(false);
      });
    }
  };

  const handleRightClick = () =>
    moveToSide(tokensList.moveRight, tokensList.addTokenToEnd);

  const handleLeftClick = () =>
    moveToSide(tokensList.moveLeft, tokensList.addTokenToBeginning);

  const update = (callback: () => void) => {
    getFreeNft(nft?.token, callback);
  };

  return {
    nft,
    noAvailable,
    error,
    loading,
    nftLoading,
    handleLeftClick,
    handleRightClick,
    update
  };
};

export default useFreeNft;
